import {
  BackButtonCWUI,
  CustomerCategoryTitleUI,
  ButtonUI,
} from '@next-order/next-order-components';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import AnimationWrapper from 'components/AnimationWrapper';
import { GeosuggestItemUI } from 'UI';
import { selectStoreConfig } from 'containers/Layout/selectors';
import { addAddressAPI, editAddressAPI } from './actions';
import MapAdjustmentView from './MapAdjustmentView';
import Map from 'components/Map';
import { ReactComponent as MarkerThemedIcon } from 'assets/images/marker-themed.svg';
import AddressAutocomplete from 'UI/GeosuggestUI/AddressAutocomplete';

export default function AddressEdit({
  address = null, // null for new address, object for editing
  userDetails,
  onBack,
  onSave,
}) {
  const storeConfig = useSelector(selectStoreConfig);
  const { orderSetup } = useSelector((state) => state.homeReducer);

  // Step management: 'address-input' or 'address-form'
  const [step, setStep] = useState(address ? 'address-form' : 'address-input');
  const [showMapAdjustment, setShowMapAdjustment] = useState(false);
  const [showScrollShadow, setShowScrollShadow] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    placeId: address?.placeId || '',
    addressLine1: address?.addressLine1 || '',
    addressLine2: address?.addressLine2 || '',
    latitude: address?.latitude || null,
    longitude: address?.longitude || null,
    apartment: address?.apartment || '',
    entryCode: address?.entryCode || '',
    buildingName: address?.buildingName || '',
    dropOffOption: address?.dropOffOption || 'hand-it-to-me',
    notes: address?.notes || '',
    label: address?.label || '',
    isDefault: address?.isDefault || false,
  });

  // Store original form data for comparison (only in edit mode)
  const [originalFormData, setOriginalFormData] = useState(null);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const isEditMode = !!address;

  const { useRadiusBasedDeliveryAreaCalulation, deliveryRadiusMts } =
    orderSetup || {};
  const radius =
    useRadiusBasedDeliveryAreaCalulation && deliveryRadiusMts
      ? deliveryRadiusMts
      : 5000;

  // Set original form data when in edit mode
  useEffect(() => {
    if (isEditMode && address) {
      const originalData = {
        addressLine1: address.addressLine1 || '',
        addressLine2: address.addressLine2 || '',
        latitude: address.latitude || null,
        longitude: address.longitude || null,
        apartment: address.apartment || '',
        entryCode: address.entryCode || '',
        buildingName: address.buildingName || '',
        dropOffOption: address.dropOffOption || 'hand-it-to-me',
        notes: address.notes || '',
        label: address.label || '',
        isDefault: address.isDefault || false,
      };
      setOriginalFormData(originalData);
    }
  }, [isEditMode, address]);

  // Check if any form values have changed from original
  const hasChanges = () => {
    if (!isEditMode || !originalFormData) return true; // Allow save for new addresses

    return Object.keys(formData).some((key) => {
      const currentValue = formData[key];
      const originalValue = originalFormData[key];

      // Handle null/undefined comparisons
      if (currentValue === null && originalValue === null) return false;
      if (currentValue === undefined && originalValue === undefined)
        return false;
      if (currentValue === null && originalValue === '') return false;
      if (currentValue === '' && originalValue === null) return false;

      return currentValue !== originalValue;
    });
  };

  // Address suggestion selection
  const updateDeliveryAddress = async (addObj) => {
    // Handle the actual structure from GeosuggestUI
    const placeId = addObj?.placeId;
    let addressLine1 = '';
    let addressLine2 = '';
    let lat = null;
    let lng = null;

    if (addObj && addObj.gmaps?.address_components) {
      const components = addObj.gmaps.address_components;

      // Extract address components
      const streetNumber =
        components.find((comp) => comp.types.includes('street_number'))
          ?.long_name || '';
      const streetName =
        components.find((comp) => comp.types.includes('route'))?.short_name ||
        '';
      const locality =
        components.find((comp) => comp.types.includes('locality'))?.long_name ||
        '';
      const state =
        components.find((comp) =>
          comp.types.includes('administrative_area_level_1')
        )?.short_name || '';
      const postalCode =
        components.find((comp) => comp.types.includes('postal_code'))
          ?.long_name || '';
      const country =
        components.find((comp) => comp.types.includes('country'))?.long_name ||
        '';

      // Construct address lines
      // Address Line 1: Street number + street name (e.g., "209 Windermere Rd")
      addressLine1 = [streetNumber, streetName].filter(Boolean).join(' ');

      // Address Line 2: Locality + State + Postal Code + Country (e.g., "Lara VIC 3212, Australia")
      const cityStateParts = [locality, state, postalCode]
        .filter(Boolean)
        .join(' ');
      addressLine2 = [cityStateParts, country].filter(Boolean).join(', ');

      // Get coordinates
      if (addObj.location) {
        lat = addObj.location.lat;
        lng = addObj.location.lng;
      } else if (addObj.gmaps?.geometry?.location) {
        lat = addObj.gmaps.geometry.location.lat;
        lng = addObj.gmaps.geometry.location.lng;
      }

      if (addressLine1 && lat !== null && lng !== null) {
        setFormData((prev) => ({
          ...prev,
          placeId,
          addressLine1,
          addressLine2,
          latitude: lat,
          longitude: lng,
        }));
        setStep('address-form');
      } else {
        console.error('Missing required address data:', {
          addressLine1,
          lat,
          lng,
        });
      }
    } else {
      // Fallback for cases where address_components is not available
      addressLine1 =
        addObj?.gmaps?.formatted_address ||
        addObj?.description ||
        addObj?.label ||
        '';
      addressLine2 = addObj?.gmaps?.vicinity || '';

      // Get coordinates
      if (addObj?.location) {
        lat = addObj.location.lat;
        lng = addObj.location.lng;
      } else if (addObj?.gmaps?.geometry?.location) {
        lat = addObj.gmaps.geometry.location.lat;
        lng = addObj.gmaps.geometry.location.lng;
      }

      if (addressLine1 && lat !== null && lng !== null) {
        setFormData((prev) => ({
          ...prev,
          placeId,
          addressLine1,
          addressLine2,
          latitude: lat,
          longitude: lng,
        }));
        setStep('address-form');
      } else {
        console.error('Missing required address data (fallback):', {
          addressLine1,
          lat,
          lng,
        });
      }
    }
  };

  const getGeoSuggestRenderItem = (suggestedAddress) => {
    return <GeosuggestItemUI>{suggestedAddress.description}</GeosuggestItemUI>;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (apiError) setApiError('');
    if (successMessage) setSuccessMessage('');
  };

  const handleDropOffOptionChange = (option) => {
    setFormData((prev) => ({
      ...prev,
      dropOffOption: option,
    }));
  };

  const handleMapAdjustment = (newLat, newLng, newAddress) => {
    if (newAddress && newAddress.place_id) {
      setFormData((prev) => ({
        ...prev,
        latitude: newLat,
        longitude: newLng,
      }));

      setFormData((prev) => ({
        ...prev,
        placeId: newAddress.place_id,
        addressLine1:
          newAddress.address_components.find((c) => c.types.includes('route'))
            ?.long_name || newAddress.formatted_address,
        addressLine2: newAddress.formatted_address
          .split(',')
          .slice(1)
          .join(','),
      }));
    } else {
      setApiError('Address not found');
    }
    setShowMapAdjustment(false);
  };

  const handleSave = async () => {
    setIsLoading(true);
    setApiError('');
    setSuccessMessage('');

    try {
      const addressData = {
        placeId: formData.placeId,
        label: formData.label,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        latitude: formData.latitude,
        longitude: formData.longitude,
        apartment: formData.apartment,
        entryCode: formData.entryCode,
        buildingName: formData.buildingName,
        dropOffOption: formData.dropOffOption,
        notes: formData.notes,
        isDefault: formData.isDefault,
      };

      if (isEditMode) {
        await editAddressAPI({
          userId: userDetails._id,
          addressId: address.id,
          addressData,
          cloudFunctionsUrl: storeConfig.cloudFunctionsUrl,
        });
        setSuccessMessage('Address updated successfully!');
      } else {
        await addAddressAPI({
          userId: userDetails._id,
          addressData,
          cloudFunctionsUrl: storeConfig.cloudFunctionsUrl,
        });
        setSuccessMessage('Address added successfully!');
      }

      // Refresh the addresses list after successful save
      try {
        if (onSave) {
          onSave();
        }
      } catch (refreshError) {
        console.error('Error refreshing addresses:', refreshError);
      }

      setTimeout(() => {
        onBack();
      }, 1500);
    } catch (error) {
      console.error('Error saving address:', error);
      // Show more detailed error messages if available
      let errorMsg =
        error?.message || 'Failed to save address. Please try again.';
      if (error?.response && error.response.message) {
        errorMsg = error.response.message;
      }
      setApiError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const getButtonText = () => {
    if (isLoading) return isEditMode ? 'Updating...' : 'Saving...';
    return isEditMode ? 'Update Address' : 'Save Address';
  };

  const isFormValid = formData.addressLine1.trim().length > 0;

  const handleScroll = (e) => {
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;

    // Show shadow when there's scrollable content and user has scrolled
    const isScrollable = scrollHeight > clientHeight;
    const hasScrolled = scrollTop > 5;

    setShowScrollShadow(isScrollable && hasScrolled);
  };

  // If showing map adjustment
  if (showMapAdjustment) {
    return (
      <MapAdjustmentView
        latitude={formData.latitude}
        longitude={formData.longitude}
        onBack={() => setShowMapAdjustment(false)}
        onSave={handleMapAdjustment}
      />
    );
  }

  // Address input step
  if (step === 'address-input') {
    return (
      <AnimationWrapper
        animation='fade-in'
        style={{
          width: '100%',
          height: '100%',
        }}
        className='profile-section'
      >
        <aside className='sidebar'>
          <div className='sidebar__wrapper'>
            <div className='card__about'>
              <div className='relative h-screen pt-4'>
                <BackButtonCWUI onClick={onBack} extendedClassName='mx-5' />

                <CustomerCategoryTitleUI extendedClassName='mt-3.25 !mb-0 ml-[-1px] px-5'>
                  Add New Address
                </CustomerCategoryTitleUI>

                <div className='px-5 mt-8'>
                  <AddressAutocomplete
                    storeConfig={storeConfig}
                    placeholder='Start typing your address...'
                    onSelect={updateDeliveryAddress}
                    onSuggestNoResults={() => {}}
                    renderSuggestItem={getGeoSuggestRenderItem}
                    errorMessage=''
                    extendedClassName='w-full border'
                    radius={radius}
                  />
                </div>
              </div>
            </div>
          </div>
        </aside>
      </AnimationWrapper>
    );
  }

  // Address form step
  return (
    <AnimationWrapper
      animation='fade-in'
      style={{
        width: '100%',
        height: '100%',
      }}
      className='profile-section'
    >
      <aside className='sidebar'>
        <div className='sidebar__wrapper'>
          <div className='card__about'>
            <div className='relative h-screen pt-4'>
              <BackButtonCWUI
                onClick={!isEditMode ? () => setStep('address-input') : onBack}
                extendedClassName='mx-5'
              />

              <div className='relative pb-4'>
                <CustomerCategoryTitleUI extendedClassName='mt-3.25 !mb-0 ml-[-1px] px-5'>
                  {isEditMode ? 'Edit Address' : 'Add Address Details'}
                </CustomerCategoryTitleUI>

                {/* Shadow below title when scrolled */}
                <div
                  className={`absolute left-0 right-0 top-full h-3 pointer-events-none transition-opacity duration-200 z-10 ${
                    showScrollShadow ? 'opacity-100' : 'opacity-0'
                  }`}
                  style={{
                    background:
                      'linear-gradient(to bottom, rgba(0, 0, 0, 0.1), transparent)',
                  }}
                />
              </div>

              {/* Scrollable Form Container */}
              <div
                className='overflow-y-auto px-5 pb-32 h-[calc(100vh-200px)] md:h-[calc(100vh-150px)] mt-0'
                onScroll={handleScroll}
              >
                {/* Address Lines Display */}
                <div className='mb-6'>
                  <label className='block font-normal mb-2 text-[17px] text-gray-900'>
                    Address
                  </label>
                  <div className='bg-gray-50 border border-gray-300 rounded-xl p-4'>
                    <div className='text-gray-900 text-sm font-medium'>
                      {formData.addressLine1}
                    </div>
                    {formData.addressLine2 && (
                      <div className='text-gray-600 mt-1'>
                        {formData.addressLine2}
                      </div>
                    )}
                  </div>
                </div>

                {/* Map Preview */}
                {formData.latitude && formData.longitude && (
                  <div className='mb-6'>
                    <label className='block font-normal mb-2 text-[17px] text-gray-900'>
                      Location
                    </label>
                    <div className='relative h-[150px] rounded-xl overflow-hidden border border-gray-300'>
                      <Map
                        lat={formData.latitude}
                        long={formData.longitude}
                        zoom={15}
                        showMarker={true}
                        markerContent={
                          <MarkerThemedIcon width={30} height={32.5} />
                        }
                        dragPan={false}
                        dragRotate={false}
                        scrollZoom={false}
                        doubleClickZoom={false}
                        touchZoom={false}
                        touchRotate={false}
                        keyboard={false}
                      />
                      <button
                        type='button'
                        onClick={() => setShowMapAdjustment(true)}
                        className='absolute top-3 right-3 bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm z-10'
                      >
                        Adjust Pin
                      </button>
                    </div>
                  </div>
                )}

                {/* Apartment/Suite and Entry Code */}
                <div className='grid grid-cols-2 gap-4 mb-6'>
                  <div>
                    <label className='block font-normal mb-2 text-[17px] text-gray-900'>
                      Apartment/Suite
                    </label>
                    <input
                      type='text'
                      className='bg-white border border-gray-300 text-gray-900 text-base rounded-xl focus:outline-none focus:ring-0 focus:border-blue-500 block w-full px-4 py-3 shadow-sm placeholder-gray-400'
                      placeholder='Apt, Suite, Unit'
                      value={formData.apartment}
                      onChange={(e) =>
                        handleInputChange('apartment', e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className='block font-normal mb-2 text-[17px] text-gray-900'>
                      Entry Code
                    </label>
                    <input
                      type='text'
                      className='bg-white border border-gray-300 text-gray-900 text-base rounded-xl focus:outline-none focus:ring-0 focus:border-blue-500 block w-full px-4 py-3 shadow-sm placeholder-gray-400'
                      placeholder='Building code'
                      value={formData.entryCode}
                      onChange={(e) =>
                        handleInputChange('entryCode', e.target.value)
                      }
                    />
                  </div>
                </div>

                {/* Building Name */}
                <div className='mb-6'>
                  <label className='block font-normal mb-2 text-[17px] text-gray-900'>
                    Building Name
                  </label>
                  <input
                    type='text'
                    className='bg-white border border-gray-300 text-gray-900 text-base rounded-xl focus:outline-none focus:ring-0 focus:border-blue-500 block w-full px-4 py-3 shadow-sm placeholder-gray-400'
                    placeholder='Building or complex name'
                    value={formData.buildingName}
                    onChange={(e) =>
                      handleInputChange('buildingName', e.target.value)
                    }
                  />
                </div>

                {/* Drop-off Options */}
                <div className='mb-6'>
                  <label className='block font-normal mb-3 text-[17px] text-gray-900'>
                    Drop-off options
                  </label>
                  <div className='flex gap-3'>
                    <button
                      type='button'
                      onClick={() => handleDropOffOptionChange('hand-it-to-me')}
                      className={`flex-1 py-3 px-4 rounded-xl border text-sm font-medium transition-colors duration-200 ${
                        formData.dropOffOption === 'hand-it-to-me'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Hand it to me
                    </button>
                    <button
                      type='button'
                      onClick={() => handleDropOffOptionChange('leave-at-door')}
                      className={`flex-1 py-3 px-4 rounded-xl border text-sm font-medium transition-colors duration-200 ${
                        formData.dropOffOption === 'leave-at-door'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Leave it at my door
                    </button>
                  </div>
                </div>

                {/* Notes */}
                <div className='mb-6'>
                  <label className='block font-normal mb-2 text-[17px] text-gray-900'>
                    Delivery instructions
                  </label>
                  <textarea
                    className='bg-white border border-gray-300 text-gray-900 text-base rounded-xl focus:outline-none focus:ring-0 focus:border-blue-500 block w-full px-4 py-3 shadow-sm placeholder-gray-400 resize-none'
                    rows={4}
                    placeholder='Add instructions'
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    style={{ height: '100px' }}
                  />
                </div>

                {/* Address Label */}
                <div className='mb-6'>
                  <label className='block font-normal mb-2 text-[17px] text-gray-900'>
                    Address Label
                  </label>
                  <input
                    type='text'
                    className='bg-white border border-gray-300 text-gray-900 text-base rounded-xl focus:outline-none focus:ring-0 focus:border-blue-500 block w-full px-4 py-3 shadow-sm placeholder-gray-400'
                    placeholder='e.g. Home, Work, Gym, School'
                    value={formData.label}
                    onChange={(e) => handleInputChange('label', e.target.value)}
                  />
                </div>

                {/* Default Address Checkbox */}
                <div className='mb-8'>
                  <label className='flex items-center cursor-pointer'>
                    <input
                      type='checkbox'
                      checked={formData.isDefault}
                      onChange={(e) =>
                        handleInputChange('isDefault', e.target.checked)
                      }
                      className='w-5 h-5 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2'
                    />
                    <span className='ml-3 mt-1 font-normal text-[17px] text-gray-900'>
                      Make this my default address
                    </span>
                  </label>
                </div>
              </div>

              {/* Fixed Save Button */}
              <div className='fixed bottom-[65px] lg:bottom-0 left-0 w-full bg-white p-5 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]'>
                {apiError && (
                  <div className='bg-red-50 border border-red-200 rounded-lg p-2 mb-3'>
                    <p className='text-red-700 text-sm text-center font-medium'>
                      {apiError}
                    </p>
                  </div>
                )}
                {successMessage && (
                  <div className='bg-green-50 border border-green-200 rounded-lg p-2 mb-3'>
                    <p className='text-green-700 text-sm text-center font-medium'>
                      {successMessage}
                    </p>
                  </div>
                )}
                <ButtonUI
                  fullWidth
                  size='xl'
                  rounded='xl'
                  variant='contained'
                  color='primary'
                  fontSize='lg'
                  extendedClassName={`text-white shadow-cw ${
                    !isFormValid || isLoading || !hasChanges()
                      ? 'opacity-50'
                      : ''
                  }`}
                  onClick={handleSave}
                  disabled={!isFormValid || isLoading || !hasChanges()}
                >
                  {getButtonText()}
                </ButtonUI>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </AnimationWrapper>
  );
}
